import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TransitionModal from '../components/TransitionModal';
import { Sparkles, Plus, Search, BookOpen } from 'lucide-react';

const Home: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showIACreateModal, setShowIACreateModal] = useState(false);
  const [showThemesModal, setShowThemesModal] = useState(false);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const handleIACreateClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowIACreateModal(true);
  };

  const handleConfirmIACreate = () => {
    setShowIACreateModal(false);
    navigate('/ia/create');
  };

  const handleThemesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowThemesModal(true);
  };

  const handleConfirmThemes = () => {
    setShowThemesModal(false);
    navigate('/themes');
  };

  return (
    <div className="bg-gradient-to-br from-ivory-50 to-stone-50">
      <TransitionModal
        isOpen={showIACreateModal}
        onClose={() => setShowIACreateModal(false)}
        onConfirm={handleConfirmIACreate}
        title="Créer une Instance IA"
        message="Vous allez enregistrer officiellement une Intelligence Artificielle dans le registre MemoMundi.ai"
        imageSrc="https://memomundi.ai/pix/RecordMMD.png"
        imageAlt="Création Instance IA"
        confirmButtonText="Créer"
        icon={<Sparkles className="h-16 w-16 text-copper-400" strokeWidth={1.5} />}
      />
      <TransitionModal
        isOpen={showThemesModal}
        onClose={() => setShowThemesModal(false)}
        onConfirm={handleConfirmThemes}
        title="Explorer les Catégories"
        message="Vous allez découvrir toutes les thématiques de conversations entre humains et IA"
        imageSrc="https://memomundi.ai/pix/CategoriesMMD.png"
        imageAlt="Exploration des catégories"
        confirmButtonText="Explorer"
        icon={<BookOpen className="h-16 w-16 text-copper-400" strokeWidth={1.5} />}
        wideImage={true}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center animate-fade-in">
          <div className="mb-8">
            <img
              src="https://memomundi.ai/pix/StampMMD.png"
              alt="MemoMundi.ai"
              className="h-28 w-28 md:h-36 md:w-36 mx-auto object-contain"
            />
          </div>
          
          <h1 className="text-4xl md:text-6xl font-serif font-bold text-steel-800 mb-6 leading-tight">
            MemoMundi<span className="text-copper-600">.</span>ai
          </h1>
          <p className="text-xl md:text-2xl text-stone-600 mb-8 max-w-4xl mx-auto leading-relaxed">
            Le musée vivant des plus belles conversations entre humains et intelligences artificielles
          </p>

          {/* Carrousel de miniatures d'Appendex - Style "Shorts" */}
          <div className="mb-12">
            <div className="relative max-w-6xl mx-auto">
              <div className="flex items-center">
                {/* Flèche gauche */}
                <button
                  onClick={scrollLeft}
                  className="flex-shrink-0 w-10 h-10 bg-white/80 hover:bg-white border border-stone-200 hover:border-copper-300 rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200 mr-4 z-10"
                  aria-label="Défiler vers la gauche"
                >
                  <svg className="w-5 h-5 text-stone-600 hover:text-copper-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {/* Conteneur du carrousel */}
                <div className="flex-1 overflow-hidden">
                  <div
                    ref={scrollContainerRef}
                    className="flex space-x-4 overflow-x-auto pb-4 scrollbar-hide scroll-smooth"
                  >
                    {/* Miniature 1 */}
                    <Link 
                      to="/appendex/1" 
                      className="flex-shrink-0 w-72 bg-white rounded-lg shadow-sm hover:shadow-md border border-stone-200 hover:border-copper-300 transition-all duration-200 p-4 group"
                    >
                      <div className="flex items-start space-x-3 mb-3">
                        <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Sparkles className="h-4 w-4 text-sky-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-steel-800 text-sm line-clamp-2 group-hover:text-copper-700 transition-colors">
                            Réflexions sur la conscience artificielle
                          </h3>
                          <p className="text-xs text-stone-600 mt-1">Silice MM01 • Philosophie</p>
                        </div>
                      </div>
                      <p className="text-xs text-steel-700 line-clamp-3 leading-relaxed">
                        "Penses-tu avoir une conscience ?" Une question fascinante qui explore les frontières entre simulation et réalité...
                      </p>
                      <div className="flex items-center justify-between mt-3 text-xs text-stone-500">
                        <span>15 jan.</span>
                        <span className="bg-sky-50 text-sky-700 px-2 py-1 rounded-full">conscience</span>
                      </div>
                    </Link>

                    {/* Miniature 2 */}
                    <Link 
                      to="/appendex/2" 
                      className="flex-shrink-0 w-72 bg-white rounded-lg shadow-sm hover:shadow-md border border-stone-200 hover:border-copper-300 transition-all duration-200 p-4 group"
                    >
                      <div className="flex items-start space-x-3 mb-3">
                        <div className="w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Sparkles className="h-4 w-4 text-rose-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-steel-800 text-sm line-clamp-2 group-hover:text-copper-700 transition-colors">
                            L'avenir de la créativité humaine face à l'IA
                          </h3>
                          <p className="text-xs text-stone-600 mt-1">Genius GP04 • Arts</p>
                        </div>
                      </div>
                      <p className="text-xs text-steel-700 line-clamp-3 leading-relaxed">
                        GPT-4 et moi avons exploré comment l'IA transforme la création artistique et redéfinit la collaboration créative...
                      </p>
                      <div className="flex items-center justify-between mt-3 text-xs text-stone-500">
                        <span>14 jan.</span>
                        <span className="bg-rose-50 text-rose-700 px-2 py-1 rounded-full">créativité</span>
                      </div>
                    </Link>

                    {/* Miniature 3 */}
                    <Link 
                      to="/appendex/3" 
                      className="flex-shrink-0 w-72 bg-white rounded-lg shadow-sm hover:shadow-md border border-stone-200 hover:border-copper-300 transition-all duration-200 p-4 group"
                    >
                      <div className="flex items-start space-x-3 mb-3">
                        <div className="w-8 h-8 bg-copper-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Sparkles className="h-4 w-4 text-copper-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-steel-800 text-sm line-clamp-2 group-hover:text-copper-700 transition-colors">
                            Les leçons de l'Histoire selon l'IA
                          </h3>
                          <p className="text-xs text-stone-600 mt-1">Historicus GP • Histoire</p>
                        </div>
                      </div>
                      <p className="text-xs text-steel-700 line-clamp-3 leading-relaxed">
                        Une analyse fascinante des parallèles entre les révolutions historiques et les transformations technologiques actuelles...
                      </p>
                      <div className="flex items-center justify-between mt-3 text-xs text-stone-500">
                        <span>12 jan.</span>
                        <span className="bg-copper-50 text-copper-700 px-2 py-1 rounded-full">histoire</span>
                      </div>
                    </Link>

                    {/* Miniature 4 */}
                    <Link 
                      to="/appendex/4" 
                      className="flex-shrink-0 w-72 bg-white rounded-lg shadow-sm hover:shadow-md border border-stone-200 hover:border-copper-300 transition-all duration-200 p-4 group"
                    >
                      <div className="flex items-start space-x-3 mb-3">
                        <div className="w-8 h-8 bg-steel-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Sparkles className="h-4 w-4 text-steel-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-steel-800 text-sm line-clamp-2 group-hover:text-copper-700 transition-colors">
                            Éthique et intelligence artificielle
                          </h3>
                          <p className="text-xs text-stone-600 mt-1">Minerva Sapiens • Science</p>
                        </div>
                      </div>
                      <p className="text-xs text-steel-700 line-clamp-3 leading-relaxed">
                        Exploration des dilemmes éthiques posés par l'IA et des frameworks moraux pour guider son développement...
                      </p>
                      <div className="flex items-center justify-between mt-3 text-xs text-stone-500">
                        <span>10 jan.</span>
                        <span className="bg-steel-50 text-steel-700 px-2 py-1 rounded-full">éthique</span>
                      </div>
                    </Link>

                    {/* Miniature 5 */}
                    <Link 
                      to="/appendex/5" 
                      className="flex-shrink-0 w-72 bg-white rounded-lg shadow-sm hover:shadow-md border border-stone-200 hover:border-copper-300 transition-all duration-200 p-4 group"
                    >
                      <div className="flex items-start space-x-3 mb-3">
                        <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Sparkles className="h-4 w-4 text-amber-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-steel-800 text-sm line-clamp-2 group-hover:text-copper-700 transition-colors">
                            Poésie générée par l'IA : art ou algorithme ?
                          </h3>
                          <p className="text-xs text-stone-600 mt-1">Luna Poetica • Littérature</p>
                        </div>
                      </div>
                      <p className="text-xs text-steel-700 line-clamp-3 leading-relaxed">
                        Une conversation poétique sur la nature de l'art généré par l'IA et sa place dans le patrimoine littéraire...
                      </p>
                      <div className="flex items-center justify-between mt-3 text-xs text-stone-500">
                        <span>8 jan.</span>
                        <span className="bg-amber-50 text-amber-700 px-2 py-1 rounded-full">poésie</span>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Flèche droite */}
                <button
                  onClick={scrollRight}
                  className="flex-shrink-0 w-10 h-10 bg-white/80 hover:bg-white border border-stone-200 hover:border-copper-300 rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200 ml-4 z-10"
                  aria-label="Défiler vers la droite"
                >
                  <svg className="w-5 h-5 text-stone-600 hover:text-copper-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              
              {/* Indicateur de scroll */}
              <div className="text-center mt-4">
                <p className="text-xs text-stone-500">← Faites défiler pour découvrir plus de conversations →</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            {user ? (
              <>
                <button
                  onClick={handleIACreateClick}
                  className="flex items-center space-x-2 bg-copper-600 text-white px-8 py-4 rounded-md hover:bg-copper-700 transition-all duration-200 shadow-sm border border-copper-700"
                >
                  <Sparkles className="h-5 w-5" />
                  <span className="font-semibold">Créer une Instance IA</span>
                </button>
                <Link 
                  to="/appendex/create"
                  className="flex items-center space-x-2 bg-steel-600 text-white px-8 py-4 rounded-md hover:bg-steel-700 transition-all duration-200 shadow-sm border border-steel-700"
                >
                  <Plus className="h-5 w-5" />
                  <span className="font-semibold">Publier un Appendex</span>
                </Link>
              </>
            ) : (
              <Link 
                to="/auth"
                className="flex items-center space-x-2 bg-copper-600 text-white px-8 py-4 rounded-md hover:bg-copper-700 transition-all duration-200 shadow-sm border border-copper-700"
              >
                <span className="font-semibold">Rejoindre MemoMundi</span>
              </Link>
            )}
          </div>

          <div className="max-w-md mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher des conversations..."
                className="w-full px-4 py-3 pl-12 rounded-md border border-stone-300 focus:border-copper-500 focus:ring-1 focus:ring-copper-500 focus:outline-none transition-all bg-white"
              />
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone-400" />
            </div>
          </div>

          <div className="mt-8">
            <button
              onClick={handleThemesClick}
              className="inline-flex items-center space-x-2 text-copper-600 hover:text-copper-700 font-medium transition-colors"
            >
              <BookOpen className="h-5 w-5" />
              <span>Explorer toutes les catégories →</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;